import { type FC, useEffect, useState } from 'react';
import { BookImage, LucideFolder, OctagonAlert } from 'lucide-react';
import useResizeObserver from 'use-resize-observer';
import { filesize } from 'filesize';
import { type ExtendedFileWithPath, useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { AssetThumbnail } from '@/components/asset-thumbnail';
import { type Asset, Variants } from '@/types/asset';
import { Progress } from '@/components/ui/progress';
import { TaskState } from '@/types/uploads';
import { NodeType } from '@/types/tree';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export const PreviewAccordion: FC<{
  asset: Asset;
  currentSelectedType: NodeType;
  selectedAssetIdsLength: number;
  temporaryAssets: Array<ExtendedFileWithPath>;
  isUpload: boolean;
  erroredAssets: any;
  assetProgress: any;
  disabled: boolean;
  activeVersion: any;
  isAssetPending: boolean;
  singleAssetView?: boolean;
}> = ({
  asset,
  currentSelectedType,
  selectedAssetIdsLength,
  temporaryAssets,
  isUpload,
  erroredAssets,
  assetProgress,
  disabled,
  activeVersion,
  isAssetPending,
  singleAssetView,
}) => {
  const tCommon = useTranslations('common');
  const tUpload = useTranslations('upload');
  const { getActiveUploadSession } = useUploadStore();
  const { session } = getActiveUploadSession();
  const { uploadState } = session;
  const temporaryType = temporaryAssets[0]?.inferredType ?? temporaryAssets[0]?.type;

  const thumbnailUrl =
    temporaryAssets[0]?.id in erroredAssets
      ? undefined
      : (asset?.variants?.[Variants.ThumbnailInternalMedium]?.url ?? temporaryAssets[0]?.preview);

  const uploadError = Boolean(erroredAssets[temporaryAssets[0]?.id]);
  const assetSize = activeVersion?.fileSize ?? temporaryAssets[0]?.size;

  const naturalWidth = asset?.variants?.[Variants.ThumbnailInternalMedium]?.width ?? 1;
  const naturalHeight = asset?.variants?.[Variants.ThumbnailInternalMedium]?.height ?? 1;
  const aspectRatio = naturalHeight / naturalWidth;

  const { ref: imageContainerRef, width: containerWidth = 1 } = useResizeObserver<HTMLDivElement>();

  const height = asset?.variants[Variants.ThumbnailInternalMedium]?.height ?? undefined;
  const width = asset?.variants[Variants.ThumbnailInternalMedium]?.width ?? undefined;

  const desiredHeight = Math.min(containerWidth * aspectRatio, 512);

  const [renderedHeight, setRenderedHeight] = useState<number>(200);

  useEffect(() => {
    if (!isAssetPending && desiredHeight > 0) {
      setRenderedHeight(desiredHeight);
    }
  }, [desiredHeight, isAssetPending]);

  if (disabled) {
    return null;
  }

  return (
    <div className="flex flex-col p-0">
      {currentSelectedType === NodeType.Assets && !singleAssetView ? (
        <AssetThumbnail
          {...(!isUpload && { id: asset?.id })}
          key={asset?.id || temporaryAssets[0]?.id}
          asset={asset}
          name={asset?.name || temporaryAssets[0]?.name}
          type={activeVersion?.mimeType ?? activeVersion?.fileType ?? temporaryType}
          thumbnail={thumbnailUrl}
          {...(!isUpload && { variantKey: Variants.PreviewInternal })}
          renderVideoPlayer
          imageContainerStyle={{ height: renderedHeight }}
          imageContainerRef={imageContainerRef}
          width={width}
          height={height}
          imageContainerClassName={cn('flex justify-center bg-ui-container relative max-h-[512px] overflow-hidden', {
            'bg-status-failure/50': uploadError,
          })}
          imageClassName={cn('size-auto object-contain', {
            'opacity-50': uploadError,
          })}
          iconContainerClassName={cn('flex w-full items-center justify-center h-auto aspect-square')}
          iconClassName={cn('stroke-ui-light icon-16', {
            'stroke-status-failure/20': uploadError,
          })}
          {...(uploadError && { overlayIcon: OctagonAlert })}
          overlayIconClassName={cn('icon-14', uploadError && 'stroke-text-base')}
        />
      ) : (
        (currentSelectedType === NodeType.Albums || currentSelectedType === NodeType.Folders) && (
          <div
            className={cn(
              'relative flex aspect-square max-h-[512px] w-full items-center justify-center overflow-hidden duration-200',
              {
                'bg-folder-background-color': currentSelectedType === NodeType.Folders,
                'bg-album-background-color': currentSelectedType === NodeType.Albums,
              },
            )}
          >
            {currentSelectedType === NodeType.Folders ? (
              <LucideFolder className="icon-16 stroke-folder-color" strokeWidth={1} />
            ) : (
              <BookImage className="icon-16 stroke-album-color" strokeWidth={1} />
            )}
          </div>
        )
      )}

      {(selectedAssetIdsLength === 1 || temporaryAssets.length > 0) && currentSelectedType === NodeType.Assets && (
        <div key={temporaryAssets[0]?.name} className="flex flex-col divide-y divide-ui-void-gap">
          {isUpload && !asset && temporaryAssets.length > 0 && erroredAssets[temporaryAssets[0]?.id] && (
            <div className="bg-status-failure p-3 text-text-inverted">
              <div className="mb-2 font-bold">{tCommon('error')}</div>
              {tUpload('fileFailedToUpload')}
            </div>
          )}

          {isUpload &&
            (uploadState === TaskState.InProgress || uploadState === TaskState.Cancelled) &&
            !(temporaryAssets[0]?.id in erroredAssets) &&
            assetProgress !== 100 && (
              <div className="flex flex-col p-3">
                <div className="flex w-full justify-between">
                  <div className="font-bold">
                    {uploadState === TaskState.Cancelled ? tUpload('stateCancelled') : tUpload('stateUploading')}
                  </div>
                  <div>{(activeVersion || temporaryAssets[0]) && filesize(assetSize)}</div>
                </div>
                {uploadState === TaskState.InProgress && (
                  <Progress value={assetProgress} className={cn('mt-2 h-[6px] w-full')} />
                )}
              </div>
            )}
        </div>
      )}
    </div>
  );
};
